import { useMemo, useEffect } from 'react'
import { useWalletStore, WalletStore } from 'store/index'
import { PROVIDER_ID, walletClients, WalletClient, walletProviders } from 'wallet/index'
import shallow from 'zustand/shallow'
import useErrorToast from './useErrorToast'

const getWalletClient = async (id: PROVIDER_ID | undefined): Promise<WalletClient> => {
  if (!id) {
    throw new Error('No wallet provider id provided')
  }

  const client = await walletClients[id]

  if (!client) {
    throw new Error(`No wallet client found for provider id: ${id}`)
  }

  return client
}

const walletProviderSelector = (state: WalletStore) => ({
  accounts: state.accounts,
  setActiveAccount: state.setActiveAccount,
  activeAccount: state.activeAccount,
  clearActiveAccount: state.clearActiveAccount,
  addAccounts: state.addAccounts,
  removeAccounts: state.removeAccounts
})

export default function useWalletManager() {
  const {
    accounts,
    setActiveAccount,
    activeAccount,
    clearActiveAccount,
    addAccounts,
    removeAccounts
  } = useWalletStore(walletProviderSelector, shallow)
  const handleError = useErrorToast()

  const accountsDependency = JSON.stringify(accounts)
  const providers = useMemo(
    () =>
      Object.values(walletProviders).map(({ id, name, icon }) => {
        return {
          id,
          name,
          icon,
          isActive: activeAccount?.providerId === id,
          isConnected: accounts.some((accounts) => accounts.providerId === id)
        }
      }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [activeAccount?.providerId, accountsDependency]
  )

  const reconnectProviders = async () => {
    Object.values(walletProviders).forEach(({ id }) => {
      reconnect(id)
    })
  }

  useEffect(() => {
    reconnectProviders()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const connect = async (id: PROVIDER_ID) => {
    const walletClient = await getWalletClient(id)
    const walletInfo = await walletClient
      ?.connect(() => disconnect(id))
      .catch((e) => {
        handleError(e)
      })

    if (walletInfo) {
      setActiveAccount(walletInfo.accounts[0])
      addAccounts(walletInfo.accounts)
    }
  }

  const reconnect = async (id: PROVIDER_ID) => {
    const walletClient = await getWalletClient(id)
    const walletInfo = await walletClient
      ?.reconnect(() => disconnect(id))
      .catch((e) => {
        console.info(`Failed to reconect ${id}`, e)
        disconnect(id)
      })

    if (walletInfo && walletInfo.accounts.length > 0) {
      addAccounts(walletInfo.accounts)
    }
  }

  const disconnect = async (id: PROVIDER_ID) => {
    const walletClient = await getWalletClient(id)
    await walletClient?.disconnect().catch((e) => {
      console.info(`Failed to disconnect ${id}`, e)
      walletClient.disconnect()
    })

    clearActiveAccount(id)
    removeAccounts(id)
  }

  const getAccountsByProvider = (id: PROVIDER_ID) => {
    return accounts.filter((account) => account.providerId === id)
  }

  return {
    providers,
    connect,
    reconnect,
    disconnect,
    accounts,
    getAccountsByProvider,
    setActiveAccount
  }
}
